import {BlobReader, BlobWriter, ZipReader, ZipWriter} from '@zip.js/zip.js'
import JSZip from 'jszip'
import {routes} from '../routing/routes'
import {showMessage} from './Alerts'

export const mimeTypes: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  gif: 'image/gif',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

export function transformDependentType(param: string): string | string[] {
  const parts = param.split('@')

  if (parts.length === 1) {
    return param
  } else {
    return parts
  }
}

export async function UnzipFiles2(
  zipFiles: File[],
  allowedMimeTypes?: string[],
  startName: boolean = false
) {
  const uZip = new JSZip()
  try {
    const unzippedFiles = await Promise.all(
      zipFiles.map(async (zipFile) => {
        const zip = await uZip.loadAsync(zipFile, {createFolders: true})
        const files = zip.files
        console.log(files)
        const filePromises = Object.values(files).map(async (file) => {
          if (!file.dir) {
            const zipName = zipFile.name.split('_').shift()
            const start = startName ? `${zipName}_` : ''
            const ext = file.name.split('.').pop() ?? ''
            const type = mimeTypes[ext] ?? ''
            const content = await file.async('blob')

            if (type) {
              return new File([content], start + file.name.replaceAll('/', '_'), {type})
            }

            return new File([content], start + file.name.replaceAll('/', '_'))
          }
        })

        return Promise.all(filePromises)
      })
    )

    const unzippedFilesArray = unzippedFiles.flat()

    if (allowedMimeTypes) {
      const unzippedFilesFiltered = unzippedFilesArray.filter((file) => {
        const mimeType = file?.type
        return allowedMimeTypes.includes(mimeType ?? '')
      })

      return unzippedFilesFiltered
    }

    return unzippedFilesArray
  } catch (error) {
    const messages = `Revise los siguientes zips: ${zipFiles
      .map((zipFile) => zipFile.name)
      .join(', ')}`
    showMessage('error', 'Hubo un error', (error as Error).message + ' - ' + messages)
    return []
  }
}

export const UnzipFiles = async (
  zipFiles: File[],
  allowedMimeTypes?: string[],
  startName = false
) => {
  const unzippedFilesPromises = zipFiles.map(async (zipFile) => {
    const entries = await new ZipReader(new BlobReader(zipFile)).getEntries()

    return await Promise.all(
      entries.map(async (file) => {
        if (file.getData && !file.directory) {
          const zipName = zipFile.name.split('_').shift()
          const start = startName ? `${zipName}_` : ''
          const ext = file.filename.split('.').pop() ?? ''
          const type = mimeTypes[ext] ?? ''
          const data = await file.getData(new BlobWriter())

          if (type) {
            return new File([data], start + file.filename.replaceAll('/', '_'), {type})
          } else {
            return new File([data], start + file.filename.replaceAll('/', '_'))
          }
        }
      })
    )
  })

  const unzippedFiles = await Promise.all(unzippedFilesPromises)
  const files = unzippedFiles.flat()

  if (allowedMimeTypes) {
    const unzippedFilesFiltered = files.filter((file) => {
      const mimeType = file?.type ?? ''
      return allowedMimeTypes.includes(mimeType)
    })

    return unzippedFilesFiltered
  }

  return files
}

export const getFilesWhenStartsWith = (files: File[], startName: string) => {
  return files
    .map((file, idx) => ({file: file, index: idx}))
    .filter((file) => file.file.name.startsWith(startName))
}

export const createZipFile = async (files: File[]) => {
  const zipWriter = new ZipWriter(new BlobWriter('application/zip'), {bufferedWrite: true})

  files.forEach((file) => {
    zipWriter.add(file.name, new BlobReader(file))
  })

  return await zipWriter.close()
}

export const formatName = (name: string) => {
  const arrayNames = name.split(' ')

  if (arrayNames.length === 2) {
    return arrayNames.join(' ')
  }

  if (arrayNames.length === 4) {
    return arrayNames[0] + ' ' + arrayNames[2]
  }

  return name
}

export const textToTitle = (text: string, splitSepator: string = ' ') => {
  const arrayWords = text.toLowerCase().split(splitSepator)

  let newText = ''
  for (const word of arrayWords) {
    const firstLetter = word.charAt(0)
    const others = word.slice(1)

    newText += firstLetter.toUpperCase() + others + ' '
  }

  return newText.trim()
}

export const getSuspender = (promise: Promise<any>) => {
  let status = 'pending'
  let result: any

  const suspender = promise.then(
    (value) => {
      status = 'success'
      result = value
    },
    (error) => {
      status = 'error'
      result = error
    }
  )

  const read = () => {
    if (status === 'pending') {
      throw suspender
    }

    if (status === 'error') {
      throw result
    }

    return result
  }

  return {read}
}

export const getDifferenceDates = (date1: string, date2: string) => {
  const d1 = new Date(date1)
  const d2 = new Date(date2)

  const diff = Math.abs(d1.getTime() - d2.getTime()) ?? 0

  if (diff === 0) return 0

  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

const cleanPath = (path: string) => path.replace(/\/:[^/]+/g, '')

export const getInfoRoute = (location: {pathname: string} | Location) => {
  const currentRouteParent = routes.find((routeParent) =>
    routeParent.children.some((route) =>
      location.pathname.startsWith(
        `${cleanPath(routeParent.path)}${route.path ? `/${cleanPath(route.path)}` : ''}`
      )
    )
  )
  const currentRoute = currentRouteParent?.children.find((route) =>
    location.pathname.startsWith(
      `${cleanPath(currentRouteParent.path)}${route.path ? `/${cleanPath(route.path)}` : ''}`
    )
  )

  return {
    parentPath: currentRouteParent?.path || null,
    path: currentRoute?.path || null,
    keypath: currentRoute?.keypath || null,
  }
}

export function generateUniqueAbbr(context: string, abbrExisted: Set<string>) {
  const words = context.trim().split(/\s+/)

  let abbr =
    words.length > 1 ? words.map((p) => p[0].toUpperCase()).join('') : words[0].toUpperCase()

  let finalAbbr = abbr
  let count = 1

  // Si ya existe, agregar sufijo incremental
  while (abbrExisted.has(finalAbbr)) {
    finalAbbr = `${abbr}${count}`
    count++
  }

  abbrExisted.add(finalAbbr)
  return finalAbbr
}

export function screamingSnakeCaseTag(tag: string): string {
  return tag
    .trim()
    .split(/\s+/)
    .map((word) => word.toUpperCase())
    .join('_')
}
